import path from "node:path";
import {
  DRIVER_DEFAULTS,
  globalSettingsPath,
  loadSettings,
  projectSettingsPath,
  resolveSettings,
  resolveSkWorkspaceConfig,
  saveSettings,
} from "@skaile/workspaces/core";
import { Command } from "commander";
import pc from "picocolors";
import { logErr, logOk, S } from "../helpers.ts";

/** Keys that belong in .skaile/settings.json (personal, gitignored). */
const PERSONAL_KEYS = new Set(["driver", "model", "provider"]);

/** API key prefix — `apiKey.<provider>` routes into the apiKeys map. */
const API_KEY_PREFIX = "apiKey.";

/** Gather workspace config defaults for display (reads agent_config.default). */
function collectWorkspaceDefaults(projectDir: string): Record<string, string> {
  const wsDefaults: Record<string, string> = {};
  try {
    const ws = resolveSkWorkspaceConfig(projectDir);
    const p = ws.agent_config?.default ?? {};
    if (p.driver) wsDefaults.driver = p.driver;
    if (p.provider) wsDefaults.provider = p.provider;
    if (p.model) wsDefaults.model = p.model;
    if (p.skills_dir) wsDefaults.skills_dir = p.skills_dir;
    if (p.agents_dir) wsDefaults.agents_dir = p.agents_dir;
    if (p.prompts_dir) wsDefaults.prompts_dir = p.prompts_dir;
  } catch {}
  return wsDefaults;
}

/** Print the personal effective-config section (driver/provider/model). */
function printEffectiveSection(config: Awaited<ReturnType<typeof resolveSettings>>): void {
  console.log();
  console.log(S.heading("  Effective Config"));
  console.log(`  ${S.rule(40)}`);
  for (const key of ["driver", "provider", "model"] as const) {
    const value = config[key];
    if (value !== undefined) {
      console.log(`  ${S.label(`${key}:`.padEnd(16))} ${value}`);
    }
  }
}

/** Print the workspace-defaults section, if any. */
function printWorkspaceDefaultsSection(wsDefaults: Record<string, string>): void {
  if (Object.keys(wsDefaults).length === 0) return;
  console.log();
  console.log(`  ${S.heading("Workspace Defaults")}  ${S.dim("(skaile.yaml)")}`);
  console.log(`  ${S.rule(40)}`);
  for (const [key, value] of Object.entries(wsDefaults)) {
    console.log(`  ${S.label(`${key}:`.padEnd(16))} ${value}`);
  }
}

/** Print the masked API-keys section, if any. */
function printApiKeysSection(config: Awaited<ReturnType<typeof resolveSettings>>): void {
  if (!config.apiKeys || Object.keys(config.apiKeys).length === 0) return;
  console.log();
  console.log(`  ${S.heading("API Keys")}`);
  console.log(`  ${S.rule(40)}`);
  for (const [provider, key] of Object.entries(config.apiKeys)) {
    const fromEnv = !!process.env[`${provider.toUpperCase()}_API_KEY`];
    if (key) {
      console.log(
        `  ${S.label(`${provider}:`.padEnd(16))} ****${key.slice(-4)}` +
          (fromEnv ? `  ${S.dim("[env]")}` : ""),
      );
    }
  }
}

/** Print the resolved-paths section for the effective driver. */
function printResolvedPathsSection(config: Awaited<ReturnType<typeof resolveSettings>>): void {
  const fw = config.driver ?? "omp";
  const paths = DRIVER_DEFAULTS[fw] ?? DRIVER_DEFAULTS.omp;
  if (!paths) return;
  console.log();
  console.log(`  ${S.heading("Resolved Paths")}  ${S.dim(`(${fw})`)}`);
  console.log(`  ${S.rule(40)}`);
  console.log(`  ${S.label("skills:".padEnd(16))} ${config.skillsDir ?? paths.skillsDir}`);
  console.log(`  ${S.label("agents:".padEnd(16))} ${config.agentsDir ?? paths.agentsDir}`);
  console.log(`  ${S.label("prompts:".padEnd(16))} ${config.promptsDir ?? paths.promptsDir}`);
}

/**
 * Render the effective config (personal + workspace layers, resolved paths,
 * masked API keys). Shared by the `config show` subcommand and the bare
 * `config` action so the latter doesn't re-drive the Commander parser.
 */
async function showConfig(opts: { projectDir?: string }): Promise<void> {
  const projectDir = opts.projectDir ? path.resolve(opts.projectDir) : process.cwd();
  const config = await resolveSettings(projectDir);
  const wsDefaults = collectWorkspaceDefaults(projectDir);

  printEffectiveSection(config);
  printWorkspaceDefaultsSection(wsDefaults);
  printApiKeysSection(config);
  printResolvedPathsSection(config);
  console.log();
}

/**
 * Create the `skaile config` command group.
 *
 * Manages personal settings stored in `.skaile/settings.json` (gitignored).
 * Subcommands: `show` (display effective config from both personal and workspace
 * layers, with resolved paths and masked API keys), `get <key>` (print a single
 * effective value), `set <key> <value>` (write a value — only `driver`, `model`,
 * `provider`, and `apiKey.<provider>` are accepted; project-level config belongs
 * in `skaile.yaml`). Pass `--global` to `set` to write to
 * `~/.skaile/settings.json` instead of the project directory.
 *
 * @returns Configured {@link Command} ready for `program.addCommand()`.
 * @docLink cli/commands/config#make-config-command
 */
export function makeConfigCommand(): Command {
  const cmd = new Command("config").description("Manage personal config (.skaile/settings.json)");

  // ── show ──────────────────────────────────────────────────────────────────

  cmd
    .command("show")
    .description("Show effective config with sources")
    .option("--project-dir <path>", "Project directory")
    .action((opts) => showConfig(opts));

  // ── get ───────────────────────────────────────────────────────────────────

  cmd
    .command("get <key>")
    .description("Get an effective config value")
    .option("--project-dir <path>", "Project directory")
    .action(async (key: string, opts) => {
      const projectDir = opts.projectDir ? path.resolve(opts.projectDir) : process.cwd();
      const config = await resolveSettings(projectDir);
      const value = (config as any)[key];
      console.log(value ?? S.dim("(not set)"));
    });

  // ── set ───────────────────────────────────────────────────────────────────

  cmd
    .command("set <key> <value>")
    .description("Set a personal config value (driver, model, provider, apiKey.<provider>)")
    .option("--project-dir <path>", "Project directory")
    .option("--global", "Write to ~/.skaile/settings.json instead of project-local")
    .action(async (key: string, value: string, opts) => {
      const projectDir = opts.projectDir ? path.resolve(opts.projectDir) : process.cwd();

      // Validate key is a personal setting
      const isApiKey = key.startsWith(API_KEY_PREFIX);
      if (!PERSONAL_KEYS.has(key) && !isApiKey) {
        logErr(
          `'${key}' is not a personal setting.\n` +
            `  Personal keys: ${[...PERSONAL_KEYS].join(", ")}, ${API_KEY_PREFIX}<provider>\n` +
            `  Project config (framework, paths, resources) belongs in skaile.yaml`,
        );
        process.exit(1);
      }

      const filePath = opts.global ? globalSettingsPath() : projectSettingsPath(projectDir);

      const existing = await loadSettings(filePath);

      if (isApiKey) {
        const provider = key.slice(API_KEY_PREFIX.length);
        existing.apiKeys = { ...existing.apiKeys, [provider]: value };
        await saveSettings(existing, filePath);
        logOk(`Set ${pc.bold(`apiKeys.${provider}`)} = ****${value.slice(-4)}`);
      } else {
        (existing as any)[key] = value;
        await saveSettings(existing, filePath);
        logOk(`Set ${pc.bold(key)} = ${value}`);
      }

      console.log(S.dim(`  → ${filePath}`));
    });

  // Bare `skaile config` shows the effective config directly — no re-parse.
  cmd.action(() => showConfig({}));

  return cmd;
}
